import { ReactNode, useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline'
import { cn } from 'src/utils/style'

interface InfoHintProps {
  /** The explanation revealed on demand. Pass already-translated copy. */
  text: ReactNode
  /** Overrides the trigger's accessible name (default "More info"). */
  label?: string
  className?: string
  'data-testid'?: string
  /** Overrides the trigger's test id (default `${data-testid}-trigger`). */
  triggerTestId?: string
  /** Test id for the revealed text. */
  contentTestId?: string
}

/**
 * A (?) trigger that reveals a short explanation. Opens on mouse hover, on
 * click/tap and on Enter/Space; closes on Escape or a tap outside.
 */
export const InfoHint = ({
  text,
  label,
  className,
  'data-testid': testId,
  triggerTestId,
  contentTestId,
}: InfoHintProps) => {
  const { t } = useTranslation()
  const [hovered, setHovered] = useState(false)
  const [pinned, setPinned] = useState(false)
  const rootRef = useRef<HTMLSpanElement>(null)
  const contentId = useId()
  const open = hovered || pinned
  const close = () => {
    setHovered(false)
    setPinned(false)
  }

  useEffect(() => {
    if (!open) return
    const close = () => {
      setHovered(false)
      setPinned(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    const onPointer = (e: PointerEvent | MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer)
    }
  }, [open])

  return (
    <span
      ref={rootRef}
      className={cn(['relative inline-flex align-middle', className])}
      data-testid={testId}
      onPointerEnter={(e) => e.pointerType === 'mouse' && setHovered(true)}
      onPointerLeave={(e) => e.pointerType === 'mouse' && setHovered(false)}
    >
      <button
        type="button"
        aria-label={label ?? t('common.moreInfo')}
        aria-expanded={open}
        aria-controls={open ? contentId : undefined}
        // A click pins a hover-opened hint; a click on a pinned one closes it.
        onClick={() => (pinned ? close() : setPinned(true))}
        className="rounded-full text-text-tertiary hover:text-text-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
        data-testid={triggerTestId ?? (testId ? `${testId}-trigger` : undefined)}
      >
        <QuestionMarkCircleIcon className="h-4 w-4" aria-hidden="true" />
      </button>
      {open && (
        <span
          id={contentId}
          role="tooltip"
          data-testid={contentTestId}
          className="absolute left-1/2 top-full z-dropdown mt-1 w-64 max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-left text-xs font-normal text-white shadow-lg"
        >
          {text}
        </span>
      )}
    </span>
  )
}
